import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import AuthContext from '../contexts/AuthContext';
import logo from '../logo.jpg';

const ROLE_TEXT = {
    STAFF: '일반 작업자',
    INTERMEDIATE_ADMIN: '중간 관리자',
    SUPER_ADMIN: '최고 관리자',
    OWNER: '소유자',
    CLIENT: '건물주',
};

const MIN_PASSWORD = 8;

/**
 * 초대 링크를 연 화면.
 *
 * 아직 계정이 없는 사람이 여는 곳이라 로그인 밖에 있다. 물어보는 것은 비밀번호
 * 하나뿐이다 -- 이름도 회사도 역할도 초대에 새겨져 있다.
 */
const InviteAcceptPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { login } = useContext(AuthContext);

    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [password, setPassword] = useState('');
    const [again, setAgain] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        api
            .get(`/invitations/token/${token}`)
            .then((res) => alive && setInvite(res.data))
            .catch(() => {
                if (!alive) return;
                // 서버는 만료인지 이미 썼는지 말하지 않는다. 화면도 그대로 둔다.
                setError('쓸 수 없는 초대입니다. 초대한 분께 다시 요청해 주세요.');
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < MIN_PASSWORD) {
            setError(`비밀번호는 ${MIN_PASSWORD}자 이상으로 정해 주세요.`);
            return;
        }
        if (password !== again) {
            setError('두 번 적은 비밀번호가 다릅니다.');
            return;
        }
        setError('');
        setSaving(true);
        try {
            await api.post(`/invitations/token/${token}/accept`, { password });
            // 방금 정한 것으로 바로 들어간다. 다시 적게 할 이유가 없다.
            const { data } = await api.post('/auth/login', {
                email: invite.email,
                password,
            });
            const me = await login(data.access_token, data.refresh_token);
            // 근무자는 오늘 할 점검이 첫 화면이다. 대시보드는 그들 것이 아니다.
            navigate(me?.role === 'STAFF' ? '/today' : '/', { replace: true });
        } catch (err) {
            setError(err.response?.data?.message || '가입하지 못했습니다.');
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center min-vh-100">
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    return (
        <div className="d-flex align-items-center justify-content-center min-vh-100 bg-light px-3">
            <div className="bg-white rounded-4 border p-4 w-100" style={{ maxWidth: '400px' }}>
                <div className="text-center mb-4">
                    <img src={logo} alt="바로보고" height="44" className="rounded mb-3" />
                    {invite ? (
                        <>
                            <h5 className="fw-bold mb-1">{invite.companyName}에 오신 것을 환영합니다</h5>
                            <p className="text-muted small mb-0">
                                {invite.name}님 · {ROLE_TEXT[invite.role] || invite.role}
                                {invite.clientName && ` · ${invite.clientName}`}
                            </p>
                        </>
                    ) : (
                        <h5 className="fw-bold mb-1">초대를 열 수 없습니다</h5>
                    )}
                </div>

                {error && <div className="alert alert-warning py-2 small">{error}</div>}

                {invite && (
                    <form onSubmit={submit}>
                        <div className="mb-3">
                            <label className="form-label fw-bold small">이메일</label>
                            {/* 바꿀 수 없다. 이 주소로 초대받았고, 이 주소로 로그인한다. */}
                            <input className="form-control bg-light" value={invite.email} disabled />
                        </div>
                        <div className="mb-3">
                            <label className="form-label fw-bold small">비밀번호</label>
                            <input
                                type="password"
                                className="form-control"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={`${MIN_PASSWORD}자 이상`}
                                autoFocus
                            />
                        </div>
                        <div className="mb-4">
                            <label className="form-label fw-bold small">비밀번호 확인</label>
                            <input
                                type="password"
                                className="form-control"
                                value={again}
                                onChange={(e) => setAgain(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn-primary w-100 fw-bold"
                            disabled={saving}
                        >
                            {saving ? '시작하는 중…' : '시작하기'}
                        </button>
                    </form>
                )}

                {!invite && (
                    <button
                        className="btn btn-white border w-100"
                        onClick={() => navigate('/login')}
                    >
                        로그인으로
                    </button>
                )}
            </div>
        </div>
    );
};

export default InviteAcceptPage;
